
import { defineComponent, ref, onMounted, onUnmounted, computed, h } from "vue";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import ApiService from "@/core/services/ApiService";
import moment from "moment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import emitter from "@/eventBus";

interface Rubric {
  name: string;
  page: string;
  file_source: string;
}

interface PrintFile {
  _id: string;
  account: string;
  date: string;
  rubrics: Rubric[];
  created_at: string;
  created_by?: string;
  updated_at?: string;
}

export default defineComponent({
  name: "media_file",
  components: {
    Datatable,
  },
  setup() {
    const checkedNews = ref<Array<string>>([]);
    const tableData = ref<Array<PrintFile>>([]);
    const tableKey = ref(0);
    const total_data = ref(0);
    const router = useRouter();
    const is_loading = ref(true);
    const store = useStore();
    const role = store.getters.currentUser.data.role;

    // @ts-ignore 
    const filterTanggal = ref<[Date, Date]>([]);
    const filterTanggalShortcut = ref([
      {
        text: "Hari Ini",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setHours(0, 0, 0);
          return [start, end];
        },
      },
      {
        text: "Kemarin",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 1);
          start.setHours(0, 0, 0);
          end.setDate(end.getDate() - 1);
          end.setHours(23, 59, 59);
          return [start, end];
        },
      },
      {
        text: "Seminggu Lalu",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - 7);
          start.setHours(0, 0, 0);
          end.setHours(23, 59, 59);
          return [start, end];
        },
      },
      {
        text: "Bulan Ini",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setDate(1);
          start.setHours(0, 0, 0);
          return [start, end];
        },
      },
      {
        text: "Bulan Lalu",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setDate(1);
          start.setMonth(start.getMonth() - 1);
          start.setHours(0, 0, 0);
          end.setDate(0);
          end.setHours(23, 59, 59);
          return [start, end];
        },
      },
      {
        text: "Tahun Ini",
        value: () => {
          const end = new Date();
          const start = new Date();
          start.setMonth(0);
          start.setDate(1);
          start.setHours(0, 0, 0);
          return [start, end];
        },
      },
    ]);
    const defaultTime = ref([
      new Date(2000, 1, 1, 0, 0, 0),
      new Date(2000, 2, 1, 23, 59, 59),
    ]);
    const itemsPerPage = ref(10);
    const currentPage = ref(1);
    const search = ref<string>("");
    const is_select_all = ref(false);

    const tableHeader = ref([
      {
        key: "checkbox",
      },
      {
        name: "Media",
        key: "account",
        sortable: true,
      },
      {
        name: "Rubrik",
        key: "rubrics",
        sortable: true,
      },
      {
        name: "Halaman",
        key: "page",
        sortable: true,
      },
      {
        name: "File",
        key: "file_source",
        sortable: true,
      },
      {
        name: "Diinput Oleh",
        key: "created_by",
        sortable: true,
      },
      {
        name: "Tanggal Publikasi",
        key: "date",
        sortable: true,
      },
      {
        name: "Actions",
        key: "actions",
      },
    ]);

    const fetchData = async () => {
      try {
        is_loading.value = true;

        const params = new URLSearchParams();

        // Tambahkan parameter dasar
        params.append('page', currentPage.value.toString());
        params.append('items', itemsPerPage.value.toString());

        // // Tambahkan filter tanggal jika ada
        if (filterTanggal.value && filterTanggal.value.length === 2) {
          params.append('from_date', filterTanggal.value[0].toISOString()); // Langsung pakai Date.toISOString()
          params.append('to_date', filterTanggal.value[1].toISOString());
        }

        // Tambahkan pencarian jika ada
        if (search.value.trim()) {
          params.append('key', search.value.trim());
        }


        // Gunakan params langsung dalam URL
        const response = await ApiService.get(`/uncropped-print-files?${params.toString()}`);

        tableData.value = response.data.uncropped_print_files;
        total_data.value = response.data.pagination_info.count;

        tableKey.value += 1;
      } catch (error) {
        Swal.fire({
          position: "top-end",
          icon: "error",
          toast: true,
          title: "Gagal memuat data",
          showConfirmButton: false,
          timer: 3000,
        });
        tableData.value = [];
        total_data.value = 0;
      } finally {
        is_loading.value = false;
      }
    };

    const deleteItem = async (id: string) => {
      Swal.fire({
        title: "Apakah anda yakin ingin menghapus data tersebut?",
        text: "Data yang dihapus tidak dapat dikembalikan",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Batal",
        confirmButtonText: "Ya, Hapus!",
      }).then(async (result) => {
        if (result.value) {
          try {
            await ApiService.delete(`/uncropped-print-files/delete/${id}`);
            Swal.fire({
              position: "top-end",
              icon: "success",
              toast: true,
              title: "Berhasil! Data berhasil dihapus",
              showConfirmButton: false,
              timer: 3000,
            });
            fetchData();
          } catch (error) {
            Swal.fire({
              position: "top-end",
              icon: "error",
              toast: true,
              title: "Gagal! Data gagal dihapus",
              showConfirmButton: false,
              timer: 3000,
            });
          }
        }
      });
    };

    const deleteSelected = () => {
      Swal.fire({
        title: "Apakah anda yakin ingin menghapus data yang dipilih?",
        text: `Anda akan menghapus ${checkedNews.value.length} data`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Batal",
        confirmButtonText: "Ya, Hapus!",
      }).then(async (result) => {
        if (result.value) {
          try {
            await ApiService.post("/uncropped-print-files/delete-many", {
              // ids: checkedNews.value,
            });
            Swal.fire({
              position: "top-end",
              icon: "success",
              toast: true,
              title: "Berhasil! Data berhasil dihapus",
              showConfirmButton: false,
              timer: 3000,
            });
            checkedNews.value = [];
            fetchData();
          } catch (error) {
            Swal.fire({
              position: "top-end",
              icon: "error",
              toast: true,
              title: "Gagal! Data gagal dihapus",
              showConfirmButton: false,
              timer: 3000,
            });
          }
        }
      });
    };

    const checkAll = () => {
      if (is_select_all.value) {
        checkedNews.value = tableData.value.map((file) => file._id);
      } else {
        checkedNews.value = [];
      }
    };

    const defaultDate = () => {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      start.setHours(0, 0, 0);
      end.setHours(23, 59, 59);
      filterTanggal.value = [start, end];
    };

    const formatDate = (date: string) => {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    };

    const currentChange = (current: number) => {
      currentPage.value = current;
      fetchData();
    };

    const itemsPerPageChange = (items: number) => {
      itemsPerPage.value = items;
      currentPage.value = 1;
      fetchData();
    };

    const searchItems = () => {
      currentPage.value = 1;
      fetchData();
    };

    const filterDateChange = () => {
      currentPage.value = 1;
      fetchData();
    };

    const viewDetail = (id: string) => {
      window.open(`/media_file/detail/${id}`, '_blank');
    };

    const editItem = (id: string) => {
      router.push(`/media_file/edit/${id}`);
    };

    onMounted(() => {
      setCurrentPageTitle(" Media File");
      defaultDate();
      fetchData();
    });

    onUnmounted(() => {
      emitter.emit("set-toolbar-content", null);
    });

    return {
      tableData,
      tableHeader,
      search,
      searchItems,
      is_select_all,
      checkedNews,
      deleteSelected,
      is_loading,
      filterTanggal,
      filterTanggalShortcut,
      defaultTime,
      currentChange,
      itemsPerPageChange,
      itemsPerPage,
      currentPage,
      formatDate,
      deleteItem,
      filterDateChange,
      total_data,
      tableKey,
      checkAll,
      viewDetail,
      editItem,
    };
  },
});
